import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowLeft } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const empty = {
  name: "", role: "", photo_url: "", bio: "",
  twitter: "", facebook: "", instagram: "", linkedin: "", order: 0,
};

export default function AdminTeam() {
  const { user, loading: authLoading } = useAuth();
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/team");
        setTeam(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (authLoading) return <p className="text-muted-foreground">Loading...</p>;
  if (!user || user === false) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <p className="text-muted-foreground">Admin only.</p>;

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (m) => {
    setEditing(m);
    setForm({ ...empty, ...m });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.put(`/team/${editing.id}`, form);
        setTeam((prev) => prev.map((m) => (m.id === data.id ? data : m)));
        toast.success("Updated");
      } else {
        const { data } = await api.post("/team", form);
        setTeam((prev) => [...prev, data]);
        toast.success("Added");
      }
      setOpen(false);
    } catch {
      toast.error("Failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (m) => {
    if (!window.confirm(`Delete ${m.name}?`)) return;
    try {
      await api.delete(`/team/${m.id}`);
      setTeam((prev) => prev.filter((x) => x.id !== m.id));
      toast.success("Deleted");
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <div>
      <div className="flex items-baseline justify-between border-b-2 border-primary pb-4 mb-8">
        <div>
          <Link to="/admin" className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.2em] text-primary mb-2 hover:underline">
            <ArrowLeft className="w-3 h-3" /> Admin
          </Link>
          <div className="font-ui text-xs uppercase tracking-[0.3em] text-muted-foreground">Team</div>
          <h1 className="font-kannada-heading text-3xl mt-1">ಉಪ ಸಂಪಾದಕರು</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="rounded-none" data-testid="admin-team-new">
              <Plus className="w-4 h-4 mr-1" /> ಸೇರಿಸಿ
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg rounded-none max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-kannada-heading text-2xl">
                {editing ? "ಸಂಪಾದಿಸಿ" : "ಹೊಸ ಸದಸ್ಯ"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={save} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest">Name</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-1" data-testid="team-name" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Role</Label>
                <Input required value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="rounded-none mt-1" data-testid="team-role" placeholder="ಉಪ ಸಂಪಾದಕ · Sub-Editor" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Photo URL</Label>
                <Input required value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} className="rounded-none mt-1" data-testid="team-photo" />
                {form.photo_url && <img src={form.photo_url} alt="" className="mt-2 h-20 w-20 object-cover rounded-sm" />}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Bio</Label>
                <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="rounded-none mt-1" rows={2} data-testid="team-bio" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest">Twitter</Label>
                  <Input value={form.twitter} onChange={(e) => setForm({ ...form, twitter: e.target.value })} className="rounded-none mt-1" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Facebook</Label>
                  <Input value={form.facebook} onChange={(e) => setForm({ ...form, facebook: e.target.value })} className="rounded-none mt-1" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Instagram</Label>
                  <Input value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} className="rounded-none mt-1" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">LinkedIn</Label>
                  <Input value={form.linkedin} onChange={(e) => setForm({ ...form, linkedin: e.target.value })} className="rounded-none mt-1" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Display Order</Label>
                <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || "0", 10) })} className="rounded-none mt-1" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none">ರದ್ದು</Button>
                <Button type="submit" disabled={saving} className="rounded-none" data-testid="team-save">
                  {saving ? "..." : "ಉಳಿಸಿ"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-border bg-card rounded-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">Photo</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Role</TableHead>
              <TableHead className="w-24">Order</TableHead>
              <TableHead className="w-24 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
            ) : team.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No team members yet.</TableCell></TableRow>
            ) : (
              team.map((m) => (
                <TableRow key={m.id} data-testid={`team-row-${m.id}`}>
                  <TableCell><img src={m.photo_url} alt="" className="w-12 h-12 object-cover rounded-sm" /></TableCell>
                  <TableCell className="font-kannada-body">{m.name}</TableCell>
                  <TableCell className="text-sm">{m.role}</TableCell>
                  <TableCell className="text-sm">{m.order}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(m)}><Pencil className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(m)}><Trash2 className="w-4 h-4 text-primary" /></Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
